/**
 * The server's `statusLabel` is a weekday and time with no date
 * (services/events-api/src/lib/statusCopy.ts `formatKickoff`); a weekday is
 * only unambiguous for today through today plus 6. Outside that window, past
 * days included, a scheduled game reads with its date in the device timezone
 * (Q271). ISSUE-1178.
 */
import { createZonedFormatterCache } from "@sportsmet/errors";

import { todayInZone } from "./calendarDay";

/** The last day, counted from today as 0, that keeps the server's weekday and time label. */
export const KICKOFF_WEEKDAY_FORM_MAX_DAYS = 6;

/** Shared per zone, never built per call. */
const kickoffWithDateFormatter = createZonedFormatterCache({
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** Whole calendar days from today to the kickoff's day, both in timeZone. Negative for a past
 *  day. null when kickoffAt does not parse. */
export function kickoffDaysFromToday(input: {
  kickoffAt: string;
  timeZone: string;
  nowMs: number;
}): number | null {
  const kickoffMs = new Date(input.kickoffAt).getTime();
  if (Number.isNaN(kickoffMs)) {
    return null;
  }
  const kickoffDay = todayInZone({
    nowMs: kickoffMs,
    timeZone: input.timeZone,
  });
  const today = todayInZone({ nowMs: input.nowMs, timeZone: input.timeZone });
  return Math.round(
    (Date.parse(`${kickoffDay}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) /
      86_400_000,
  );
}

/** "Sat, Oct 24 · 7:00 PM" in timeZone, or "" when kickoffAt does not parse. */
export function formatKickoffWithDate(input: {
  kickoffAt: string;
  timeZone: string;
}): string {
  const kickoffMs = new Date(input.kickoffAt).getTime();
  if (Number.isNaN(kickoffMs)) {
    return "";
  }
  const parts = kickoffWithDateFormatter(input.timeZone).formatToParts(
    new Date(kickoffMs),
  );
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  const hour = parts.find((p) => p.type === "hour")?.value ?? "";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "";
  const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value ?? "";
  return `${weekday}, ${month} ${day} · ${hour}:${minute} ${dayPeriod}`;
}

/** The label a fan reads for a game's kickoff (ISSUE-1178). */
export function resolveKickoffLabel(input: {
  status: string;
  statusLabel: string;
  kickoffAt: string;
  timeZone: string;
  nowMs: number;
}): string {
  if (input.status !== "scheduled") {
    return input.statusLabel;
  }
  const days = kickoffDaysFromToday({
    kickoffAt: input.kickoffAt,
    timeZone: input.timeZone,
    nowMs: input.nowMs,
  });
  if (days === null) {
    return input.statusLabel;
  }
  if (days >= 0 && days <= KICKOFF_WEEKDAY_FORM_MAX_DAYS) {
    return input.statusLabel;
  }
  return formatKickoffWithDate({
    kickoffAt: input.kickoffAt,
    timeZone: input.timeZone,
  });
}
